import { useState, useEffect, useMemo, useRef } from 'react'
import { transactionFields } from '../../../../constants/forms/almoxarifado/transactionFields'
import { useSectionFields } from '../../../../hooks/useSectionFields'
import DocumentTypeEnums from '../../../../enums/document-enum'

/**
 * @param {Array} items - Array de itens selecionados
 * @param {Array} equipments - Array de equipamentos selecionados
 * @param {boolean} isEntrada - Indica se a transação é de entrada
 * @param {boolean} isTransferencia - Indica se a transação é de transferência
 * @param {Array} products - Array de produtos disponíveis
 * @param {Object} productsSelect - Objeto de seleção de produtos
 * @param {Function} handleItemChange - Função para alterar o estado do item
 * @param {Object} addressesSelect - Objeto de seleção de endereços
 * @param {Object} locationsSelect - Objeto de seleção de localizações
 * @param {Object} equipmentAddressesSelect - Objeto de seleção de endereços de equipamentos
 * @param {Object} equipmentLocationsSelect - Objeto de seleção de localizações de equipamentos
 * @param {boolean} isEditPage - Indica se a página é de edição
 * @param {Object} docTypeSelect - Objeto de seleção de tipo de documento
 * @returns {Object} - Objeto com os estados e funções para a lógica da página
 */
export const useTransactionPageLogic = (
  items,
  equipments,
  isEntrada,
  isTransferencia,
  products,
  productsSelect,
  setFormData,
  handleItemChange,
  addressesSelect,
  locationsSelect,
  equipmentAddressesSelect,
  equipmentLocationsSelect,
  isEditPage,
  docTypeSelect
) => {
  const [transactionSection, productsSection, equipmentsSection] =
    transactionFields
  const [transactionFieldsState, setTransactionFieldsState] = useState(
    transactionSection.fields
  )
  const [productsFieldsState, setProductsFieldsState] = useState(
    productsSection.fields
  )
  const [equipmentsFieldsState, setEquipmentsFieldsState] = useState(
    equipmentsSection.fields
  )
  const { updateField: updateProps, removeFields } = useSectionFields()

  const [isEquipmentSelected, setIsEquipmentSelected] = useState(false)
  const [isInsumoSelected, setIsInsumoSelected] = useState(false)
  const [, setIsCustomerSelected] = useState(false)

  const [senderSelectedValue, setSenderSelectedValue] = useState(false)
  const [receiverSelectedValue, setReceiverSelectedValue] = useState(false)
  const [isBuyerSelected, setIsBuyerSelected] = useState(false)

  const [transactionTypeSelectedValue, setTransactionTypeSelectedValue] =
    useState(false)

  // Atualiza os campos da seção de produtos com base no tipo de transação
  useEffect(() => {
    let updatedProductFields = [...productsSection.fields]
    let updatedTransactionFields = [...transactionSection.fields]
    let updatedEquipmentFields = [...equipmentsSection.fields]

    // Se não houver tipo de transação selecionado, remove os campos e deixar somente os campos de tipo de transação
    if (!transactionTypeSelectedValue) {
      updatedProductFields = removeFields({
        arrayDependente: updatedProductFields,
        field_id: [
          'items.product_item_id',
          'items.product_id',
          'items.equipment_id',
          'items.category_id',
          'items.condition_id',
          'items.address_id',
          'items.location_id',
          'items.warranty_date',
          'items.unit_cost',
          'items.quantity',
        ],
      })

      updatedTransactionFields = updateProps({
        arrayDependente: updatedTransactionFields,
        field_id: ['transaction.transaction_type_id'],
        updates: {
          fullWidth: true,
        },
      })

      updatedTransactionFields = removeFields({
        arrayDependente: updatedTransactionFields,
        field_id: [
          'transaction.transaction_date',
          'transaction.doc_type_id',
          'transaction.supplier_id',
          'transaction.doc_number',
          'transaction.doc_date',
          'transaction.amount_total',
          'transaction.amount_discount',
          'transaction.purchase_date',
          'transaction.asset_type',
          'transaction.sender_id',
          'transaction.receiver_id',
          'transaction.buyer_id',
          'transaction.details',
          'transaction.customer_id',
          'transaction.sender_address_id', // ⬅️ Adicionado
          'transaction.receiver_address_id', // ⬅️ Adicionado
        ],
      })

      updatedEquipmentFields = removeFields({
        arrayDependente: updatedEquipmentFields,
        field_id: ['equipments.equipment_id'],
      })
    }

    if (isEntrada) {
      updatedTransactionFields = removeFields({
        arrayDependente: updatedTransactionFields,
        field_id: [
          'transaction.asset_type',
          'transaction.sender_id',
          'transaction.receiver_id', // <-- organização de destino não é necessária
          'transaction.customer_id',
          'transaction.address_id',
          'transaction.sender_address_id', // <-- remove origem
          // NÃO remover 'transaction.receiver_address_id' (será usado como endereço de destino)
        ],
      })

      // * Se o tipo de documento for diferente de NOTA FISCAL, esconder os campos (doc_number, amount_total, amount_discount)
      if (
        docTypeSelect.selectedValue?.value !== DocumentTypeEnums['NOTA FISCAL']
      ) {
        updatedTransactionFields = removeFields({
          arrayDependente: updatedTransactionFields,
          field_id: [
            'transaction.doc_number',
            'transaction.amount_total',
            'transaction.amount_discount',
          ],
        })

        // * Define o campo buyer_id com o fullWidth
        updatedTransactionFields = updateProps({
          arrayDependente: updatedTransactionFields,
          field_id: ['transaction.buyer_id'],
          updates: {
            fullWidth: true,
          },
        })
      }

      updatedProductFields = removeFields({
        arrayDependente: updatedProductFields,
        field_id: ['items.product_item_id'],
      })
    }

    if (
      docTypeSelect.selectedValue?.value === DocumentTypeEnums['NOTA FISCAL']
    ) {
      // * Define o campo purchase_date com o fullWidth
      updatedTransactionFields = updateProps({
        arrayDependente: updatedTransactionFields,
        field_id: ['transaction.purchase_date'],
        updates: {
          fullWidth: true,
        },
      })
    }

    if (isTransferencia) {
      updatedProductFields = removeFields({
        arrayDependente: updatedProductFields,
        field_id: [
          'items.product_id',
          'items.warranty_date',
          'items.unit_cost',
        ],
      })

      updatedProductFields = updateProps({
        arrayDependente: updatedProductFields,
        field_id: ['items.category_id', 'items.condition_id'],
        updates: {
          disabled: false,
        },
      })

      updatedProductFields = updateProps({
        arrayDependente: updatedProductFields,
        field_id: ['items.quantity'],
        updates: {
          fullWidth: true,
        },
      })

      updatedTransactionFields = removeFields({
        arrayDependente: updatedTransactionFields,
        field_id: [
          'transaction.supplier_id',
          'transaction.buyer_id',
          'transaction.doc_type_id',
          'transaction.doc_number',
          'transaction.doc_date',
          'transaction.purchase_date',
          'transaction.customer_id',
          'transaction.amount_total',
          'transaction.amount_discount',
        ],
      })

      updatedTransactionFields = updateProps({
        arrayDependente: updatedTransactionFields,
        field_id: ['transaction.asset_type'],
        updates: {
          fullWidth: true,
        },
      })
    }

    if (isEditPage) {
      const config = {
        isEntrada: {
          transaction: ['transaction.supplier_id', 'transaction.buyer_id'],
          product: [
            'items.product_id',
            'items.category_id',
            'items.condition_id',
            'items.address_id',
            'items.location_id',
          ],
          equipment: [],
        },
        isTransferencia: {
          transaction: [
            'transaction.supplier_id',
            'transaction.receiver_id',
            'transaction.customer_id',
            'transaction.amount_total',
            'transaction.amount_discount',
          ],
          product: [
            'items.category_id',
            'items.condition_id',
            'items.address_id',
            'items.location_id',
          ],
          equipment: ['equipments.address_id', 'equipments.location_id'],
        },
      }

      const types = [
        { flag: isEntrada, key: 'isEntrada' },
        { flag: isTransferencia, key: 'isTransferencia' },
      ]

      types.forEach(({ flag, key }) => {
        if (!flag) return

        const {
          transaction: tFields,
          product: pFields,
          equipment: eFields,
        } = config[key]

        if (tFields.length) {
          updatedTransactionFields = updateProps({
            arrayDependente: updatedTransactionFields,
            field_id: tFields,
            updates: { disabled: true },
          })
        }

        if (pFields.length) {
          updatedProductFields = updateProps({
            arrayDependente: updatedProductFields,
            field_id: pFields,
            updates: { disabled: true },
          })
        }

        if (eFields.length) {
          updatedEquipmentFields = updateProps({
            arrayDependente: updatedEquipmentFields,
            field_id: eFields,
            updates: { disabled: true },
          })
        }
      })
    }

    if (items.length > 0) {
      updatedTransactionFields = updateProps({
        arrayDependente: updatedTransactionFields,
        field_id: updatedTransactionFields.map((field) => field.id),
        updates: { disabled: true },
      })
    }

    setProductsFieldsState(updatedProductFields)
    setTransactionFieldsState(updatedTransactionFields)
    setEquipmentsFieldsState(updatedEquipmentFields)
  }, [
    isEntrada,
    isTransferencia,
    transactionTypeSelectedValue,
    removeFields,
    updateProps,
    productsSection.fields,
    transactionSection.fields,
    equipmentsSection.fields,
    isEditPage,
    docTypeSelect.selectedValue?.value,
    items,
  ])

  // * Preencher o campo de "Valor total dos itens automaticamente" quando insumo e transação de entrada

  // Preenche dados do produto selecionado
  useEffect(() => {
    if (!Array.isArray(products)) return

    const productSelected = products.find(
      (product) => product.id === productsSelect.selectedValue?.value
    )

    if (!productSelected) return

    const itemDataFromProduct = productSelected.items?.[0] || {}

    if (!isEntrada && !isEditPage) {
      handleItemChange('items.address_id', {
        value: itemDataFromProduct.address_id,
        label: itemDataFromProduct.address?.formatted_address,
      })

      handleItemChange('items.location_id', {
        value: itemDataFromProduct.location_id,
        label: itemDataFromProduct.location?.formatted_location,
      })
    }
  }, [
    productsSelect.selectedValue,
    isEntrada,
    products,
    isEditPage,
    handleItemChange,
  ])

  // Reseta localização ao alterar endereço do insumo
  const prevAddressValue = useRef(addressesSelect.selectedValue?.value)
  useEffect(() => {
    if (prevAddressValue.current !== addressesSelect.selectedValue?.value) {
      if (
        prevAddressValue.current !== undefined &&
        locationsSelect.selectedValue
      ) {
        locationsSelect?.reset()
      }
      prevAddressValue.current = addressesSelect.selectedValue?.value
    }
  }, [addressesSelect.selectedValue?.value, locationsSelect])

  // Reseta localização ao alterar endereço do equipamento
  const prevAddressValueEquipment = useRef(
    equipmentAddressesSelect.selectedValue?.value
  )
  useEffect(() => {
    if (
      prevAddressValueEquipment.current !==
      equipmentAddressesSelect.selectedValue?.value
    ) {
      equipmentLocationsSelect?.reset()
      prevAddressValueEquipment.current =
        equipmentAddressesSelect.selectedValue?.value
    }
  }, [equipmentAddressesSelect.selectedValue?.value, equipmentLocationsSelect])

  // Desabilita botão de adicionar item sem tipo de transação selecionado
  const isDisabledButton = useMemo(() => {
    if (isEditPage) {
      return {
        equipamento: true,
        insumo: true,
      }
    }

    if (
      (isTransferencia && (!senderSelectedValue || !receiverSelectedValue)) ||
      senderSelectedValue ||
      receiverSelectedValue ||
      (isEquipmentSelected && isInsumoSelected)
    ) {
      return {
        equipamento:
          !senderSelectedValue ||
          !receiverSelectedValue ||
          !isEquipmentSelected,
        insumo:
          !senderSelectedValue || !receiverSelectedValue || !isInsumoSelected,
      }
    }

    if (isEntrada && isBuyerSelected) {
      return { equipamento: false, insumo: !isEntrada || !isBuyerSelected }
    }

    return { equipamento: true, insumo: true }
  }, [
    isEditPage,
    isEquipmentSelected,
    isInsumoSelected,
    isTransferencia,
    senderSelectedValue,
    receiverSelectedValue,
    isEntrada,
    isBuyerSelected,
  ])

  return {
    transactionFieldsState,
    productsFieldsState,
    equipmentsFieldsState,
    isDisabledButton,
    setIsEquipmentSelected,
    setIsInsumoSelected,
    setTransactionTypeSelectedValue,
    setSenderSelectedValue,
    setReceiverSelectedValue,
    setIsCustomerSelected,
    setIsBuyerSelected,
  }
}
